import {
  CurecallButton,
  ECurecallButtonAppearance,
  ECurecallNotificationAppearance,
  UploadIcon,
} from '@honestica/curecall-ui'
import { AdminOnlyBadge } from '../admin-only-badge.component'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useRef, useState } from 'react'
import {
  useCurrentProject,
  WithFeatureEnabled,
} from 'src/providers/current-project.provider'
import { useNotification } from 'src/providers/notification.provider'
import { PatientService } from 'src/services/patient.service'
import TextareaForChat from '../textarea-for-chat/textarea-for-chat.component'
import { EOperatingSystem, getOS } from 'src/utils/navigator.util'
import { twMerge } from 'src/utils/tw-merge.util'

type Props = {
  patientId: number
}

const CurecallMessenger = ({ patientId }: Readonly<Props>) => {
  const { t } = useTranslation()
  const { project } = useCurrentProject()
  const [patientResponse, setPatientResponse] = useState('')
  const { notify } = useNotification()
  const imageInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  const textMutation = useMutation({
    mutationFn: (event: any) => {
      event?.preventDefault()
      if (!patientResponse.length) {
        throw new Error('Cannot send empty message')
      }
      return PatientService.postASMSAsPatient(project.id, patientId, { message: patientResponse })
    },
    onError: () => {
      notify({
        title: t('components.sms.curecall-messenger-sent-error-title'),
        message: t('components.sms.curecall-messenger-error-sent'),
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    },
    onSuccess: async () => {
      setPatientResponse('')
      notify({
        title: t('components.sms.curecall-messenger-sent-success-title'),
        message: t('components.sms.curecall-messenger-success-sent'),
        appearance: ECurecallNotificationAppearance.SUCCESS,
      })
    },
  })

  const imageMutation = useMutation({
    mutationFn: (file: File) => PatientService.postImageAsPatient(project.id, patientId, file),
    onError: () => {
      notify({
        title: t('components.sms.curecall-messenger-image-sent-error-title'),
        message: t('components.sms.curecall-messenger-image-error-sent'),
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-sms', project.id, patientId] })
      notify({
        title: t('components.sms.curecall-messenger-image-sent-success-title'),
        message: t('components.sms.curecall-messenger-image-success-sent'),
        appearance: ECurecallNotificationAppearance.SUCCESS,
      })
    },
  })

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    imageMutation.mutate(file)
    // Reset so the same file can be re-selected
    e.target.value = ''
  }

  return (
    <form method="POST" action="#" onSubmit={textMutation.mutate} className="c-flex c-flex-col c-w-full">
      <TextareaForChat
        textareaClassName="c-w-full tw-h-16"
        value={patientResponse}
        onChange={(e: any) => setPatientResponse(e.target.value)}
        label={
          <span className="c-flex c-items-center c-gap-1">
            <AdminOnlyBadge />
            {t('components.sms.curecall-messenger-label')}
          </span>
        }
        onCmdEnter={textMutation.mutate}
        required={true}
      />
      <div className="c-flex c-items-center c-justify-end c-mt-2 c-gap-3">
        <div
          className={twMerge(
            'c-transition-opacity c-duration-1000 tw-mt-1 c-text-sm c-text-grey-500',
            patientResponse.length > 0 ? 'c-opacity-100' : 'c-opacity-0',
          )}
        >
          {getOS() === EOperatingSystem.macOS
            ? t('components.sms.curecall-messenger-shortcut-hint-macos')
            : t('components.sms.curecall-messenger-shortcut-hint')}
        </div>

        <WithFeatureEnabled featuresAccepted={['patient-documents']}>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*,application/pdf"
            className="c-hidden"
            onChange={handleImageChange}
          />
          <CurecallButton
            appearance={ECurecallButtonAppearance.SECONDARY}
            type="button"
            isLoading={imageMutation.isPending}
            onClick={() => imageInputRef.current?.click()}
            className="c-self-end"
          >
            <span className="c-flex c-items-center c-gap-1">
              <AdminOnlyBadge variant="button" className="c-h-3 c-w-3 c-mt-0" />
              <UploadIcon className="c-h-3 c-w-3 tw-w-0" />
              {t('components.sms.curecall-messenger-send-image-label')}
            </span>
          </CurecallButton>
        </WithFeatureEnabled>

        <CurecallButton
          appearance={ECurecallButtonAppearance.SECONDARY}
          type="submit"
          isLoading={textMutation.isPending}
          className="c-self-end"
        >
          {t('components.sms.curecall-messenger-button')}
        </CurecallButton>
      </div>
    </form>
  )
}

export default CurecallMessenger
